
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'

declare class ResizeObserver {
    constructor(callback: () => void)
    observe(target: Element): void
    disconnect(): void
}

let counter = 0

@Options({
    name: 'MarqueeText',
})
export default class MarqueeText extends Vue {
    @Prop({ type: Number, default: 30 }) speed!: number
    @Prop({ type: Boolean, default: true }) animate!: boolean

    animName = ''
    containerW = 0
    textW = 0
    hasAnimatedOnce = false
    styleEl: HTMLStyleElement | null = null
    ro: ResizeObserver | null = null
    measureDebounce: ReturnType<typeof setTimeout> | null = null

    get isStatic(): boolean {
        return !this.animate && !this.hasAnimatedOnce
    }

    get spanStyle(): Record<string, string> {
        if (!this.containerW || !this.textW) {
            return { display: 'inline-block', whiteSpace: 'nowrap' }
        }
        if (this.isStatic) {
            return {
                display: 'inline-block',
                whiteSpace: 'nowrap',
                animation: 'none',
                transform: 'none',
            }
        }
        const duration = (this.containerW + this.textW) / this.speed
        return {
            display: 'inline-block',
            whiteSpace: 'nowrap',
            animationName: this.animName,
            animationDuration: `${duration}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            animationPlayState: this.animate ? 'running' : 'paused',
        }
    }

    mounted(): void {
        counter++
        this.animName = `marquee_text_${counter}`
        this.hasAnimatedOnce = this.animate
        this.styleEl = document.createElement('style')
        document.head.appendChild(this.styleEl)
        this.$nextTick(() => {
            this.measure()
            this.ro = new ResizeObserver(() => this.measure())
            const c = this.$refs.container as HTMLElement
            const t = this.$refs.text as HTMLElement
            if (c) this.ro.observe(c)
            if (t) this.ro.observe(t)
        })
    }

    beforeUnmount(): void {
        if (this.measureDebounce) clearTimeout(this.measureDebounce)
        if (this.ro) this.ro.disconnect()
        if (this.styleEl) this.styleEl.remove()
    }

    updated(): void {
        this.$nextTick(() => this.scheduleMeasure())
    }

    @Watch('speed')
    onSpeedChange(): void {
        this.measure()
    }

    @Watch('animate')
    onAnimateChange(next: boolean): void {
        if (next) {
            this.hasAnimatedOnce = true
            this.$nextTick(() => this.measure())
        }
    }

    scheduleMeasure(): void {
        if (this.measureDebounce) clearTimeout(this.measureDebounce)
        this.measureDebounce = setTimeout(() => {
            this.measureDebounce = null
            this.measure()
        }, 100)
    }

    measure(): void {
        const container = this.$refs.container as HTMLElement | undefined
        const text = this.$refs.text as HTMLElement | undefined
        if (!container || !text) return
        const cw = container.offsetWidth
        const tw = text.scrollWidth
        if (cw === 0 || tw === 0) return
        this.containerW = cw
        this.textW = tw
        this.writeKeyframe()
    }

    writeKeyframe(): void {
        if (!this.styleEl) return
        if (
            !this.containerW ||
            !this.textW
        ) {
            this.styleEl.innerHTML = ''
            return
        }
        const cw = this.containerW
        const tw = this.textW
        this.styleEl.innerHTML = `
            @keyframes ${this.animName} {
                from { transform: translateX(${cw}px); }
                to   { transform: translateX(-${tw}px); }
            }
        `
    }
}
